/**
 * Pure rules of the project Pages sidebar tree, shared by the tree itself and
 * the tab's default selection / expansion, so what is selected and what is
 * shown first can't disagree (DEV13-120, DEV13-123).
 */

interface TreePage {
  id: number;
  parent?: number | null;
}

/** The tree's top level, in list order: true top-level pages plus orphans
 *  whose parent isn't in `pages` (e.g. an archived parent), so archiving a
 *  parent lifts its children up instead of hiding them. */
export function treeRoots<T extends TreePage>(pages: T[]): T[] {
  const ids = new Set(pages.map(p => p.id));
  return pages.filter(p => p.parent == null || !ids.has(p.parent));
}

/** The page a tab opens on with no `?page=`: the first row the sidebar shows —
 *  its first top-level page, or the first page of a flat list (archived). */
export function defaultPageId(pages: TreePage[], flat: boolean): number | null {
  return (flat ? pages[0] : treeRoots(pages)[0])?.id ?? null;
}

/** `startId` and every ancestor above it, nearest first. Stops at a missing
 *  parent and at a cycle, so bad data can't loop. */
export function ancestorChain(
  byId: ReadonlyMap<number, TreePage>,
  startId: number | null | undefined
): number[] {
  const chain: number[] = [];
  const seen = new Set<number>();
  let cur = startId ?? null;
  while (cur != null && !seen.has(cur)) {
    seen.add(cur);
    chain.push(cur);
    cur = byId.get(cur)?.parent ?? null;
  }
  return chain;
}
